console.log("FindHumane launched");

import * as React from 'react';
import 'react-native-get-random-values';
import { Platform, StyleSheet, View } from 'react-native';
import { AppWrapper } from "./js/screens/AppWrapper";
import * as AppState from "./js/context/AppState";
import { Provider } from 'react-redux';
import * as AppUtilities from "./js/context/AppUtilities";
import * as AppConstants from "./js/context/AppConstants";
import * as SplashScreen from 'expo-splash-screen';
import { CustomActivityIndicator } from "./js/components/CustomActivityIndicator";
import * as AppCache from "./js/context/AppCache";

SplashScreen.preventAutoHideAsync();

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

function isBooleanTrue(urlParams, name) {
  const val = urlParams.get(name);
  if (val && val.length > 0 && val.toLowerCase() != 'n') {
    return true;
  } else {
    return false;
  }
}

export default function App() {
  const [appState, setAppState] = React.useState(null);

  React.useEffect(() => {
    AppUtilities.info("App started. version: " + AppConstants.VERSION + ", platform: " + Platform.OS);

    const bootstrapAsync = async () => {

      let temporaryAppState = Object.assign({}, AppConstants.DEFAULT_STATE);

      try {
        try {
          AppUtilities.info("App.bootstrapAsync Core info: width: " + AppUtilities.getWidth() + ", height: " + AppUtilities.getHeight());
        } catch (e) {
          AppUtilities.handleException(e, "App.bootstrapAsync Core info");
        }
        try {
          const localData = await AppUtilities.loadLocalData();
          if (localData) {
            AppUtilities.info("App.bootstrapAsync Saved state:");
            AppUtilities.dir(localData);

            let savedAppState = localData[AppConstants.LOCAL_DATA_KEYS.SAVED_APP_STATE];
            if (savedAppState) {
              AppUtilities.info("App.bootstrapAsync Merging saved app state");

              if (savedAppState.dataFilter) {
                savedAppState.dataFilter.qid = AppUtilities.getUUID();
              }

              temporaryAppState = Object.assign({}, temporaryAppState, savedAppState);

              AppUtilities.dir(temporaryAppState);
            }
          }
        } catch (localDataException) {
          if (localDataException.message && localDataException.message.includes("Failed to read the 'localStorage' property from 'Window': Access is denied for this document")) {
            // Some browser disallow localStorage. Don't bother reporting on this.
            AppUtilities.info("App.bootstrapAsync Could not read localStorage: " + AppUtilities.getErrors(localDataException));
          } else {
            AppUtilities.handleException(localDataException, "App.bootstrapAsync loadLocalData");
          }
        }

        if (AppUtilities.isWeb()) {
          // Load up any query parameters
          if (window && window.location && window.location.search) {
            try {
              const urlParams = new URLSearchParams(window.location.search);

              if (isBooleanTrue(urlParams, "indoorsplus")) {
                AppUtilities.info("App.bootstrapAsync indoorsplus set");

                temporaryAppState.dataFilter = Object.assign(
                  {},
                  AppConstants.clearBuiltinOptions(temporaryAppState.dataFilter),
                  AppConstants.getBuiltinOptionsIndoorsPlus(),
                );

                // If the main filter type is specified in the URL, then
                // we saved it off permanently
                AppUtilities.updateSavedAppState("dataFilter", temporaryAppState.dataFilter);
              }

              if (isBooleanTrue(urlParams, "outdoorsplus")) {
                AppUtilities.info("App.bootstrapAsync outdoorsplus set");

                temporaryAppState.dataFilter = Object.assign(
                  {},
                  AppConstants.clearBuiltinOptions(temporaryAppState.dataFilter),
                  AppConstants.getBuiltinOptionsOutdoorsPlus(),
                );

                // If the main filter type is specified in the URL, then
                // we saved it off permanently
                AppUtilities.updateSavedAppState("dataFilter", temporaryAppState.dataFilter);
              }

              if (isBooleanTrue(urlParams, "pasture")) {
                AppUtilities.info("App.bootstrapAsync pasture set");

                temporaryAppState.dataFilter = Object.assign(
                  {},
                  AppConstants.clearBuiltinOptions(temporaryAppState.dataFilter),
                  AppConstants.getBuiltinOptionsPasture(),
                );

                // If the main filter type is specified in the URL, then
                // we saved it off permanently
                AppUtilities.updateSavedAppState("dataFilter", temporaryAppState.dataFilter);
              }

              for (var key of Object.keys(AppConstants.WELFARE_CERTIFICATIONS)) {
                var val = AppConstants.WELFARE_CERTIFICATIONS[key];
                var dbName = val[AppConstants.TYPE_KEYS.DATABASE_KEY];
                var urlKey = val[AppConstants.TYPE_KEYS.URL_KEY];
                if (isBooleanTrue(urlParams, dbName) || isBooleanTrue(urlParams, urlKey)) {
                  temporaryAppState.dataFilter[val[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
                }
              }

              for (var key of Object.keys(AppConstants.EXTRA_ANIMAL_WELFARE)) {
                var val = AppConstants.EXTRA_ANIMAL_WELFARE[key];
                var dbName = val[AppConstants.TYPE_KEYS.DATABASE_KEY];
                var urlKey = val[AppConstants.TYPE_KEYS.URL_KEY];
                if (isBooleanTrue(urlParams, dbName) || isBooleanTrue(urlParams, urlKey)) {
                  temporaryAppState.dataFilter[val[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
                }
              }

              for (var key of Object.keys(AppConstants.INGREDIENTS)) {
                var val = AppConstants.INGREDIENTS[key];
                var dbName = val[AppConstants.TYPE_KEYS.DATABASE_KEY];
                var urlKey = val[AppConstants.TYPE_KEYS.URL_KEY];
                if (isBooleanTrue(urlParams, dbName) || isBooleanTrue(urlParams, urlKey)) {
                  temporaryAppState.dataFilter[val[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
                }
              }

              for (var key of Object.keys(AppConstants.LOCATION_FLAGS)) {
                var val = AppConstants.LOCATION_FLAGS[key];
                var dbName = val[AppConstants.TYPE_KEYS.DATABASE_KEY];
                var urlKey = val[AppConstants.TYPE_KEYS.URL_KEY];
                if (isBooleanTrue(urlParams, dbName) || isBooleanTrue(urlParams, urlKey)) {
                  temporaryAppState.dataFilter[val[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
                }
              }

              const q = urlParams.get('q');
              if (q && q.length > 0 && q != "All Categories") {
                AppUtilities.info("App.bootstrapAsync Search query: " + q);
                temporaryAppState.dataFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]] = q;
              }

              const lat = urlParams.get('lat');
              const lng = urlParams.get('lng');
              if (lat && lat.length > 0 && lng && lng.length > 0) {
                AppUtilities.info("App.bootstrapAsync Query center: " + lat + "," + lng);
                temporaryAppState.initialCenter = {
                  lat: parseFloat(lat),
                  lng: parseFloat(lng),
                };
                temporaryAppState.shouldAskToMoveToCurrentLocation = false;
              }

              const zoom = urlParams.get('zoom');
              if (zoom && zoom.length > 0) {
                AppUtilities.info("App.bootstrapAsync Query zoom: " + zoom);
                temporaryAppState.initialZoom = parseInt(zoom);
              }

              if (isBooleanTrue(urlParams, "noz")) {
                AppUtilities.info("App.bootstrapAsync nozoom");
                temporaryAppState.shouldAskToMoveToCurrentLocation = false;
              }

              /*
              const locationSearch = urlParams.get('l');
              if (locationSearch && locationSearch.length > 0) {
                AppUtilities.info("App.bootstrapAsync Query location search: " + locationSearch);
                temporaryAppState.initialLocationSearch = locationSearch;
              }
              */

              const deliverySearch = urlParams.get('d');
              if (deliverySearch && deliverySearch.length > 0) {
                AppUtilities.info("App.bootstrapAsync Delivery search: " + deliverySearch);
                temporaryAppState.deliveryLocationFilter = deliverySearch;
              }

              const producerSearch = urlParams.get('producer');
              if (producerSearch && producerSearch.length > 0) {
                AppUtilities.info("App.bootstrapAsync Producer search: " + producerSearch);
                temporaryAppState.dataFilter[AppConstants.SYNTHESIZED_TYPES.PRODUCER[AppConstants.TYPE_KEYS.URL_KEY]] = producerSearch;
              }

              const productSearch = urlParams.get('product');
              if (productSearch && productSearch.length > 0) {
                AppUtilities.info("App.bootstrapAsync Product search: " + productSearch);
                temporaryAppState.dataFilter[AppConstants.SYNTHESIZED_TYPES.PRODUCT[AppConstants.TYPE_KEYS.URL_KEY]] = productSearch;
              }

              /*
              const locationNameSearch = urlParams.get('location');
              if (locationNameSearch && locationNameSearch.length > 0) {
                AppUtilities.info("App.bootstrapAsync Location name search: " + locationNameSearch);
                temporaryAppState.search = locationNameSearch;
                temporaryAppState.searchLocation = locationNameSearch;
                temporaryAppState.filter = Object.assign({}, temporaryAppState.filter, { searchlocation: locationNameSearch });
                temporaryAppState.filterForLocation = Object.assign({}, temporaryAppState.filterForLocation, { searchlocation: locationNameSearch });
              }
              */

              if (isBooleanTrue(urlParams, "debug")) {
                AppUtilities.info("App.bootstrapAsync Debug enabled");
                AppState.setGlobalState(AppConstants.SIMPLE_GLOBAL_STATE.DEFAULT_SHOW_DEBUG, true);
              }

              if (isBooleanTrue(urlParams, "extradebug")) {
                AppUtilities.info("App.bootstrapAsync Extra debug enabled");
                AppState.setGlobalState(AppConstants.SIMPLE_GLOBAL_STATE.DEFAULT_SHOW_DEBUG, true);
                temporaryAppState.extraDebug = true;
                AppUtilities.setExtraDebug(true);
              }

              if (isBooleanTrue(urlParams, "light")) {
                AppUtilities.info("App.bootstrapAsync Light mode enabled");
                temporaryAppState.lightMode = true;
              }

              if (isBooleanTrue(urlParams, "notabs")) {
                AppUtilities.info("App.bootstrapAsync No tabs");
                temporaryAppState.showTabs = false;
              }

              if (isBooleanTrue(urlParams, "nozbtns")) {
                AppUtilities.info("App.bootstrapAsync No zoom buttons");
                temporaryAppState.showZoomControls = false;
              }

              if (isBooleanTrue(urlParams, "nosearch")) {
                AppUtilities.info("App.bootstrapAsync No search");
                temporaryAppState.shouldShowSearch = false;
              }

              if (isBooleanTrue(urlParams, "noheader")) {
                AppUtilities.info("App.bootstrapAsync No header");
                temporaryAppState.showHeader = false;
              }

              if (isBooleanTrue(urlParams, "nofilterpicker")) {
                AppUtilities.info("App.bootstrapAsync No filter picker");
                temporaryAppState.showFilterSlider = false;
              }

              if (isBooleanTrue(urlParams, "reset")) {
                AppUtilities.info("App.bootstrapAsync Reset saved state");
                
                temporaryAppState = Object.assign({}, AppConstants.DEFAULT_STATE);
                await AppUtilities.removeLocalData();
              }
            } catch (paramsError) {
              AppUtilities.handleException(paramsError, "App.bootstrapAsync error parsing params");
            }
          }
        } else {
          // Nothing native-specific for now
        }

        try {
          await AppCache.initialize();
        } catch (cacheException) {
          AppUtilities.handleException(cacheException, "App.bootstrapAsync cache initialize");
        }
      } catch (e) {
        AppUtilities.handleException(e, "App.bootstrapAsync load stored state");
      }

      AppUtilities.info("App.bootstrapAsync Finished bootstrap1");

      if (AppUtilities.DEBUGLEVEL.relativelyImportant) {
        AppUtilities.debug("App.bootstrapAsync app state:");
        AppUtilities.dir(temporaryAppState);
      }

      setAppState(temporaryAppState);
    };

    bootstrapAsync();
  }, []);

  if (!appState) {
    // Splash screen should still be showing in the phone app and hidden in AppWrapper;
    // however, for web, we should show something
    if (AppUtilities.isWeb()) {
      return (
        <View style={styles.loadingContainer}>
          <CustomActivityIndicator dark={true} />
        </View>
      );
    } else {
      return null;
    }
  } else {
    return (
      <Provider store={AppState.createStore(appState)}>
        <AppWrapper />
      </Provider>
    );
  }
}
